import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { getApi, postApi, putApi, deleteApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
const { Option } = Select;

const CellRemarkModal = ({ 
  isOpen, 
  onClose, 
  cellData, 
  onRemarkAdded,
  existingRemarks = [] 
}) => {
  const [remark, setRemark] = useState('');
  const [tagAll, setTagAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [viewMode, setViewMode] = useState(existingRemarks.length > 0 ? 'view' : 'add');
  const [editingRemarkId, setEditingRemarkId] = useState(null);
  
  // Get current user ID
  const currentUser = JSON.parse(localStorage.getItem('userDetail') || '{}');
  const currentUserId = currentUser?.id;

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      if (existingRemarks.length === 0) {
        setViewMode('add');
      }
    }
  }, [isOpen, existingRemarks]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await getApi('/cell-remark/users');
      if (response.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!remark.trim()) {
      toast.error('Please enter a remark');
      return;
    }

    if (!tagAll && selectedUsers.length === 0) {
      toast.error('Please tag at least one user or select "Tag All"');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        ...cellData,
        remark: remark.trim(),
        tag_all: tagAll,
        tagged_users: tagAll ? [] : selectedUsers,
      };

      let response;
      if (editingRemarkId) {
        response = await putApi(`/cell-remark/${editingRemarkId}`, payload);
      } else {
        response = await postApi('/cell-remark', payload);
      }

      if (response.success) {
        toast.success(editingRemarkId ? 'Remark updated successfully' : 'Remark added successfully');
        if (onRemarkAdded) {
          onRemarkAdded(response.data.cellRemark);
        }
        handleClose();
      } else {
        toast.error(response.message || 'Failed to save remark');
      }
    } catch (error) {
      console.error('Error saving remark:', error);
      toast.error('Failed to save remark');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    if (!window.confirm('Are you sure you want to delete this remark?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await deleteApi(`/cell-remark/${remarkId}`);
      
      if (response.success) {
        toast.success('Remark deleted successfully');
        if (onRemarkAdded) {
          onRemarkAdded(null); // Trigger refresh
        }
        handleClose();
      } else {
        toast.error(response.message || 'Failed to delete remark');
      }
    } catch (error) {
      console.error('Error deleting remark:', error);
      toast.error('Failed to delete remark');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRemark = (remarkItem) => {
    setEditingRemarkId(remarkItem.id);
    setRemark(remarkItem.remark);
    setTagAll(remarkItem.tag_all);
    setSelectedUsers(remarkItem.tagged_users || []);
    setViewMode('add');
  };

  const handleClose = () => {
    setRemark('');
    setTagAll(false);
    setSelectedUsers([]);
    setEditingRemarkId(null);
    setViewMode(existingRemarks.length > 0 ? 'view' : 'add');
    onClose();
  };

  const getCellTypeLabel = (cellType) => {
    const labels = {
      'arrival_info': 'Arrival Info',
      'sales_reserve': 'Sales Reserve',
      'arrival_qty': 'Arrival Qty',
      'total_qty': 'Total Qty',
      'estimate_stock': 'Estimate Stock',
      'convert_in': 'Convert In',
      'convert_out': 'Convert Out',
    };
    return labels[cellType] || cellType;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay custom-overlay cell-remark-modal-overlay">
      <div className="modal-content custom-modal cell-remark-modal">
      <SimpleBar style={{ maxHeight: 500 }}>
        <div className="modal-header">
          <h3>{viewMode === 'add' ? (editingRemarkId ? 'Edit Remark' : 'Add Remark') : 'View Remarks'}</h3>
          <img
            src={window.getPublicUrl('/assets/images/close.svg')}
            onClick={handleClose}
            className="close"
            alt="Close"
          />
        </div>

        <div className="cell-remark-modal-body">
          {/* Cell Information */}
          <div className="cell-info-section">
            <h4>Cell Information</h4>
            <div className="cell-info-grid">
              <div className="cell-info-item">
                <span className="label">Type:</span>
                <span className="value">{getCellTypeLabel(cellData?.cell_type)}</span>
              </div>
              <div className="cell-info-item">
                <span className="label">Date:</span>
                <span className="value">{cellData?.date}</span>
              </div>
              {/* {cellData?.product_id && (
                <div className="cell-info-item">
                  <span className="label">Product:</span>
                  <span className="value">{cellData?.product_name || cellData?.product_id}</span>
                </div>
              )} */}
              {cellData?.product_group_no ? (
                <div className="cell-info-item">
                  <span className="label">Product Group No:</span>
                  <span className="value">{cellData?.product_group_no}</span>
                </div>
              ):cellData?.product_id && (
                <div className="cell-info-item">
                  <span className="label">Product:</span>
                  <span className="value">{cellData?.product_name || cellData?.product_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* View Mode: Show existing remarks */}
          {viewMode === 'view' && (
            <div className="remarks-list-section">
              <div className="remarks-header">
                <h4>Existing Remarks ({existingRemarks.length})</h4>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => setViewMode('add')}
                >
                  Add New Remark
                </button>
              </div>
              
              <div className="remarks-list">
                {existingRemarks.map((remarkItem) => (
                  <div key={remarkItem.id} className="remark-item">
                    <div className="remark-header">
                      <div className="remark-author">
                        <strong>{remarkItem.creator?.name || 'Unknown'}</strong>
                        <span className="remark-date">
                          {new Date(remarkItem.created_at).toLocaleString()}
                        </span>
                      </div>
                      {/* Only show edit/delete buttons if current user is the creator */}
                      {(remarkItem.creator?.id === currentUserId || remarkItem.created_by === currentUserId) && (
                        <div className="remark-actions">
                          <button 
                            className="btn-icon"
                            onClick={() => handleEditRemark(remarkItem)}
                            title="Edit"
                          >
                            <img src={window.getPublicUrl('/assets/images/edit.svg')} alt="Edit" />
                          </button>
                          <button 
                            className="btn-icon"
                            onClick={() => handleDeleteRemark(remarkItem.id)}
                            title="Delete"
                          >
                            <img src={window.getPublicUrl('/assets/images/delete.svg')} alt="Delete" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="remark-content">
                      {remarkItem.remark}
                    </div>
                    <div className="remark-tags">
                      {remarkItem.tag_all ? (
                        <span className="tag tag-all">Tagged: All Users</span>
                      ) : (
                        <span className="tag">
                          Tagged: {remarkItem.tagged_users?.length || 0} user(s)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add/Edit Mode: Form */}
          {viewMode === 'add' && (
            <form onSubmit={handleSubmit} className="remark-form">
              {existingRemarks.length > 0 && (
                <button 
                  type="button"
                  className="btn btn-sm btn-secondary mb-3"
                  onClick={() => {
                    setEditingRemarkId(null);
                    setRemark('');
                    setTagAll(false);
                    setSelectedUsers([]);
                    setViewMode('view');
                  }}
                >
                  ← Back to View
                </button>
              )}

              <div className="form-group">
                <label htmlFor="remark">Remark *</label>
                <textarea
                  id="remark"
                  className="form-control"
                  rows="4"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Enter your remark here..."
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label checbox-cs relative cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tagAll}
                    onChange={(e) => {
                      setTagAll(e.target.checked);
                      if (e.target.checked) {
                        setSelectedUsers([]);
                      }
                    }}
                    disabled={loading}
                  />
                  <span className="checkmark"></span>
                  <span>Tag All Users</span>
                </label>
              </div>

              {!tagAll && (
                <div className="form-group">
                  <label htmlFor="taggedUsers">Tag Users *</label>
                  <Select
                    id="taggedUsers"
                    mode="multiple"
                    showSearch
                    placeholder="Select users to tag"
                    value={selectedUsers}
                    onChange={setSelectedUsers}
                    optionFilterProp="children"
                    loading={usersLoading}
                    disabled={loading || usersLoading}
                    style={{ width: '100%' }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    {users.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </Option>
                    ))}
                  </Select>
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingRemarkId ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          )}
        </div>
    </SimpleBar>
      </div>
    </div>
  );
};

export default CellRemarkModal;



